import { Node } from '@tiptap/core';
import api from '../../../services/api';

/**
 * Matches a ticket key like RGDP-228, TP-4, ABC-1000 at the end of a string.
 * Pattern: 1+ uppercase letters, optional digits, dash, 1+ digits.
 */
const TICKET_KEY_REGEX = /([A-Z][A-Z0-9]*-\d+)$/;

/**
 * TipTap extension: TicketReference
 *
 * When the user types a ticket key (e.g. "RGDP-228") and presses Space,
 * the extension fetches the ticket from the API and replaces the raw text
 * with a styled inline chip showing: [key: title] [STATUS].
 *
 * Stored as a <span data-ticket-key="..."> in HTML, re-parsed on load.
 */
export const TicketReferenceExtension = Node.create({
  name: 'ticketReference',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      taskKey: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-ticket-key'),
        renderHTML: (attrs) => ({ 'data-ticket-key': attrs.taskKey || '' }),
      },
      taskId: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-ticket-id'),
        renderHTML: (attrs) => ({ 'data-ticket-id': String(attrs.taskId || '') }),
      },
      title: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-ticket-title'),
        renderHTML: (attrs) => ({ 'data-ticket-title': attrs.title || '' }),
      },
      status: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-ticket-status'),
        renderHTML: (attrs) => ({ 'data-ticket-status': attrs.status || '' }),
      },
      projectId: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-project-id'),
        renderHTML: (attrs) => ({ 'data-project-id': String(attrs.projectId || '') }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-ticket-key]' }];
  },

  renderHTML({ node }) {
    const { taskKey, taskId, title, status, projectId } = node.attrs;
    const href =
      projectId && taskKey ? `/projects/${projectId}/board/${taskKey}` : '#';
    const statusLabel = status
      ? status.replace(/_/g, ' ').toUpperCase()
      : '';

    const linkContent = [
      ['span', { class: 'ticket-ref-key' }, taskKey || ''],
      title ? ['span', { class: 'ticket-ref-title' }, `: ${title}`] : null,
    ].filter(Boolean);

    const outerChildren = [
      ['a', { class: 'ticket-ref-link', href }, ...linkContent],
      statusLabel
        ? ['span', { class: `ticket-ref-status ticket-ref-status--${(status || '').toLowerCase()}` }, statusLabel]
        : null,
    ].filter(Boolean);

    return [
      'span',
      {
        class: 'ticket-ref',
        'data-ticket-key': taskKey || '',
        'data-ticket-id': String(taskId || ''),
        'data-ticket-title': title || '',
        'data-ticket-status': status || '',
        'data-project-id': String(projectId || ''),
        contenteditable: 'false',
      },
      ...outerChildren,
    ];
  },

  addKeyboardShortcuts() {
    return {
      Space: () => {
        _handleSpaceKey(this.editor);
        return false; // allow space to be inserted as normal
      },
    };
  },
});

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function _handleSpaceKey(editor) {
  const { state } = editor;
  const { $from } = state.selection;

  // Only act when inside a text block (not code blocks, etc.)
  if (!$from.parent || !$from.parent.isTextblock) return;
  if ($from.parent.type.spec.code) return;

  let textBeforeCursor;
  try {
    textBeforeCursor = state.doc.textBetween($from.start(), $from.pos);
  } catch (_e) {
    return;
  }

  const match = textBeforeCursor.match(TICKET_KEY_REGEX);
  if (!match) return;

  const ticketKey = match[1];
  const ticketKeyStart = $from.pos - ticketKey.length;

  // Space hasn't been inserted yet — use setTimeout(0) so space inserts first,
  // then we replace [ticketKey + space] with [node + space].
  setTimeout(() => _fetchAndReplace(editor, ticketKey, ticketKeyStart), 0);
}

async function _fetchAndReplace(editor, ticketKey, startPos) {
  try {
    const response = await api.get(`/tasks/${ticketKey}`);
    const task = response.data;

    // Verify the ticket key text is still at the expected position.
    // (User may have continued typing before the request resolved.)
    const { doc } = editor.state;
    const endPos = startPos + ticketKey.length;

    let textAtPos;
    try {
      textAtPos = doc.textBetween(startPos, endPos);
    } catch (_e) {
      return;
    }
    if (textAtPos !== ticketKey) return;

    // endPos + 1 = position after the space that was just inserted
    editor
      .chain()
      .deleteRange({ from: startPos, to: endPos + 1 })
      .insertContentAt(startPos, [
        {
          type: 'ticketReference',
          attrs: {
            taskKey: task.task_key,
            taskId: task.id,
            title: task.title,
            status: task.status,
            projectId: task.project_id,
          },
        },
        { type: 'text', text: ' ' },
      ])
      .run();
  } catch (_err) {
    // Ticket not found or network error — leave the typed text as-is.
  }
}
